<template>
    <div class="fees-by-product w-full d-flex flex-column">
        <!-- Original product-based chart -->
        <AonCol class="aon-col-12 px-0">
            <AonCard title="Upcoming Fees - By Product">
                <div class="inner-content">
                    <div id="feesBarChart"></div>
                </div>
            </AonCard>
            <AonCoverLoading
                :loading="props.data.length === 0 && !props.loading"
                :show-spinner="false"
                :title="`No Fees By Product`"
                :message="`No fees were found for ${entityStore.entity?.name} between ${portfolioManagerStore.dateRange[0]} and ${portfolioManagerStore.dateRange[1]}`"
            />
        </AonCol>
    </div>
</template>

<script setup>
import { watch, computed, nextTick, onUnmounted } from 'vue'
import { useEntityStore, usePortfolioManagerStore } from '@/stores'

import * as am5 from '@amcharts/amcharts5'
import { useBarChart } from '@/composables/charts/barChart'
import { config } from '@/config'

import moment from 'moment'

const entityStore = useEntityStore()
const portfolioManagerStore = usePortfolioManagerStore()

let rootProduct
let barChartProduct

const emit = defineEmits(['item-clicked'])
const props = defineProps({
    data: {
        type: Array,
        default: () => [],
    },
    loading: {
        type: Boolean,
        default: false,
    },
    showProductLabels: {
        type: Boolean,
        default: true,
    },
})

watch(
    () => props.data,
    () => {
        initializeCharts()
    }
)

onUnmounted(() => {
    if (rootProduct) rootProduct.dispose()
})

const productData = computed(() => {
    // Convert to the format expected by the bar chart
    return props.data
        .map((entry) => {
            return {
                name: entry.product,
                value: entry.upcomingFeeAmount,
                dateRange: {
                    from: portfolioManagerStore.dateRange[0],
                    to: portfolioManagerStore.dateRange[1],
                },
            }
        })
        .sort((a, b) => a.value - b.value)
})

const initializeCharts = async () => {
    // Initialize product chart
    if (rootProduct) {
        rootProduct.dispose()
    }

    await nextTick()

    am5.addLicense(config.license.AMChartsLicense)

    // Create product chart
    if (productData.value.length > 0) {
        rootProduct = am5.Root.new('feesBarChart')
        barChartProduct = useBarChart(rootProduct)

        barChartProduct.setClickCallback(handleBarClick)

        barChartProduct.configureBar({ horizontal: true, scrollable: true })
        barChartProduct.generateAxes(productData.value, {
            showBullets: props.showProductLabels,
            horizontal: true,
            formatCurrency: true,
            scrollable: true,
        })
        barChartProduct.generateSeries(productData.value, {
            horizontal: true,
            formatCurrency: true,
            clickable: true,
        })
    }
}

const handleBarClick = (barData) => {
    if (barData.date) {
        delete barData.name
        delete barData.originalData.name
    }
    emit('item-clicked', barData)
}
</script>

<style lang="scss" scoped>
.fees-by-product {
    width: 100%;
    position: relative;

    .card-content {
        width: 100%;
    }

    #feesMonthlyBarChart {
        width: 100%;
        min-height: 200px;

        div {
            height: 100%;
        }
    }

    #feesBarChart {
        width: 100%;
        min-height: 400px;

        div {
            height: 100%;
        }
    }
}
</style>
